import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import { selectTimeZoneOptions } from '../../../redux/data/selectors';
import { COLORS } from '../../../constants/styles';
import SelectCustom from '../../SelectCustom';
import TextInput from '../../TextInput';
import Button from '../../Button';
import Icon from '../../Icon';
import ValidationError from '../ValidationError';
import TimePicker from '../TimePicker';
import Popup from '../Popup';
import styles from './index.styl';
import {
  handleInitialTime,
  parseValueToField,
  parseValueToSelectTimeZone,
} from './utils';

export function TimePickerRange({
  label,
  value,
  onChange,
  validateTime,
  formatMessage,
  withCurrentTime,
  saveButtonTitle = formatMessage({ id: 'common.ok' }),
  cancelButtonTitle = formatMessage({ id: 'common.cancel' }),
}) {
  const [isStartTimePopupVisible, toggleStartTimeVisibility] = useState(false);
  const [isEndTimePopupVisible, toggleEndTimeVisibility] = useState(false);
  const [timeZone, setTimeZone] = useState(
    parseValueToSelectTimeZone(value) || null,
  );

  const timeZonesOption = useSelector(selectTimeZoneOptions);

  const actualValueStart = withCurrentTime
    ? handleInitialTime()
    : parseValueToField(value, true);

  const actualValueEnd = withCurrentTime
    ? handleInitialTime()
    : parseValueToField(value, false);

  const [pickerTimeStart, setTimeStart] = useState(actualValueStart);
  const [pickerTimeEnd, setTimeEnd] = useState(actualValueEnd);
  const [error, showError] = useState(null);

  const toggleStartPopup = useCallback(() => {
    toggleStartTimeVisibility(!isStartTimePopupVisible);
    showError(null);
  }, [isStartTimePopupVisible, toggleStartTimeVisibility]);

  const toggleEndPopup = useCallback(() => {
    toggleEndTimeVisibility(!isEndTimePopupVisible);
    showError(null);
  }, [isEndTimePopupVisible, toggleEndTimeVisibility]);

  const handleSaveStart = () => {
    const errorMessage = validateTime && validateTime(pickerTimeStart);

    if (!errorMessage) {
      onChange([
        {
          value: pickerTimeStart,
        },
        { value: pickerTimeEnd },
        { value: timeZone },
      ]);
      toggleStartTimeVisibility(false);
    } else {
      showError(errorMessage);
    }
  };

  const handleSaveEnd = () => {
    const errorMessage = validateTime && validateTime(pickerTimeEnd);

    if (!errorMessage) {
      onChange([
        {
          value: pickerTimeStart,
        },
        { value: pickerTimeEnd },
        { value: timeZone },
      ]);
      toggleEndTimeVisibility(false);
    } else {
      showError(errorMessage);
    }
  };

  const handleResetValue = () => {
    setTimeStart(null);
    setTimeEnd(null);
    setTimeZone(null);
    onChange([
      {
        value: null,
      },
      { value: null },
      { value: null },
    ]);
  };

  const handleSetValue = (startTime, endTime, isStartTime) => {
    if (isStartTime) {
      setTimeStart(moment(startTime, 'h:mm a').format('h:mm A'));
    } else {
      setTimeEnd(moment(endTime, 'h:mm a').format('h:mm A'));
    }
  };

  const validateRangeTime = (startTime, endTime, isStartTime) => {
    if (startTime && endTime) {
      const parseTimeStart = moment(startTime, 'HH:mm a');
      const parseTimeEnd = moment(endTime, 'HH:mm a');
      const isTimeStartBeforeTimeEnd = parseTimeStart.isBefore(parseTimeEnd);
      const isTimeEndAfterTimeStart = parseTimeEnd.isAfter(parseTimeStart);

      if (!isTimeStartBeforeTimeEnd && !isTimeEndAfterTimeStart) {
        showError(
          formatMessage({
            id: isStartTime
              ? 'profile.employee.workingHours.startTime.error'
              : 'profile.employee.workingHours.endTime.error',
          }),
        );
      } else {
        showError(null);
        handleSetValue(startTime, endTime, isStartTime);
      }
    } else {
      handleSetValue(startTime, endTime, isStartTime);
    }
  };

  return (
    <div className={styles.root}>
      <span className={styles.label}>{label}</span>
      <div className={styles.textInputWrapper}>
        <TextInput
          rounded
          name="startTime"
          type="text"
          value={pickerTimeStart}
          onFocus={toggleStartPopup}
          placeholder={formatMessage({
            id: 'profile.fields.workingHours.startTime',
          })}
          className="mr2"
          readOnly
        />
        <TextInput
          rounded
          name="endTime"
          type="text"
          value={pickerTimeEnd}
          onFocus={toggleEndPopup}
          placeholder={formatMessage({
            id: 'profile.fields.workingHours.endTime',
          })}
          readOnly
        />
        <Button
          icon
          raised
          className={styles.iconReset}
          disabled={!timeZone && !pickerTimeStart && !pickerTimeEnd}
        >
          <Icon
            name="ico-24-close"
            size={16}
            onClick={() => handleResetValue()}
            style={{ fill: COLORS.stormyBlue }}
          />
        </Button>
      </div>
      <SelectCustom
        id="timeZone"
        name="timeZone"
        className="mt3"
        value={timeZone ? timeZone.value : null}
        label={formatMessage({ id: 'profile.fields.workingHours.timeZone' })}
        placeholder={formatMessage({
          id: 'profile.fields.workingHours.timeZone',
        })}
        needUpdateDefaultValue
        options={timeZonesOption}
        canSearch
        onChange={item => {
          setTimeZone(item);
          onChange([
            {
              value: pickerTimeStart,
            },
            { value: pickerTimeEnd },
            { value: item },
          ]);
        }}
      />

      <Popup
        opened={isStartTimePopupVisible}
        white
        width="rubber"
        enabled
        withHeading={false}
        contentNoPadding
        mobileFullScreen={false}
      >
        <TimePicker
          time={pickerTimeStart}
          onChange={val => {
            validateRangeTime(val, pickerTimeEnd, true);
          }}
        />
        <If condition={error}>
          <ValidationError content={error} className={styles.validationError} />
        </If>
        <div className="pt3 pb1 flex justify-end">
          <Button color="transparent" onClick={toggleStartPopup}>
            {cancelButtonTitle}
          </Button>
          <Button
            color="transparent"
            onClick={handleSaveStart}
            disabled={!!error}
          >
            {saveButtonTitle}
          </Button>
        </div>
      </Popup>
      <Popup
        opened={isEndTimePopupVisible}
        white
        width="rubber"
        enabled
        withHeading={false}
        contentNoPadding
        mobileFullScreen={false}
      >
        <TimePicker
          time={pickerTimeEnd}
          onChange={val => {
            validateRangeTime(pickerTimeStart, val, false);
          }}
        />
        <If condition={error}>
          <ValidationError content={error} className={styles.validationError} />
        </If>
        <div className="pt3 pb1 flex justify-end">
          <Button color="transparent" onClick={toggleEndPopup}>
            {cancelButtonTitle}
          </Button>
          <Button
            color="transparent"
            onClick={handleSaveEnd}
            disabled={!!error}
          >
            {saveButtonTitle}
          </Button>
        </div>
      </Popup>
    </div>
  );
}

TimePickerRange.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  cancelButtonTitle: PropTypes.string,
  saveButtonTitle: PropTypes.string,
  validateTime: PropTypes.func,
  formatMessage: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  withCurrentTime: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/common/TimePickerRange/index.jsx